import {IFlow} from "../../../../../data/interfaces/iflow.interface";

// Mismo tope que valida el app-backend (MAX_DOCUMENT_BYTES en
// app/validations/document.validation.js, 20 MB = 20,000,000 bytes, el de
// siempre): si se cambia allá, se cambia aquí para que el aviso salga antes
// de subir.
export const TAMANO_MAXIMO_DOCUMENTO = 20 * 1000000
export const TAMANO_MAXIMO_DOCUMENTO_TEXTO = '20 MB'

// El modelo acepta de 1 a 12 archivos por paso; cualquier otra cosa (ausente,
// nula, basura) se trata como el paso de un archivo de siempre.
export const cantidadArchivos = (paso: Pick<IFlow, 'fileCount'> | null | undefined): number => {
  const cantidad = Math.floor(Number(paso?.fileCount))
  if (!Number.isFinite(cantidad) || cantidad < 1) return 1
  return Math.min(cantidad, 12)
}

// Con cuántos archivos ya pasa el paso (rango «de 4 a 6»). Misma regla que
// minimoDeArchivos del app-backend (app/services/resolverArchivosDelPaso.js):
// un entero de 1 a la cantidad (también como texto, «4»); cualquier otra cosa
// (ausente, 0, 2.5, mayor que la cantidad) pide todos, como siempre.
export const minimoArchivos = (paso: Pick<IFlow, 'fileCount' | 'fileCountMin'> | null | undefined): number => {
  const cantidad = cantidadArchivos(paso)
  const valor = paso?.fileCountMin as unknown
  const minimo = valor === null || valor === undefined || valor === '' || typeof valor === 'boolean' ? NaN : Number(valor)
  return Number.isInteger(minimo) && minimo >= 1 ? Math.min(minimo, cantidad) : cantidad
}

// El paso pide un rango: se completa antes de subir todos (los demás son opcionales).
export const tieneRangoArchivos = (paso: Pick<IFlow, 'fileCount' | 'fileCountMin'> | null | undefined): boolean =>
  minimoArchivos(paso) < cantidadArchivos(paso)

export const etiquetaArchivo =(paso: Pick<IFlow, 'fileLabels'> | null | undefined, indice: number): string => {
  const etiqueta = (paso?.fileLabels?.[indice] ?? '').trim()
  return etiqueta || `Archivo ${indice + 1}`
}

// Cuenta los índices que ya tienen archivo y no están rechazados: es la misma
// cuenta con la que el backend da el paso por completo.
export const archivosCargados = (paso: Pick<IFlow, 'fileCount' | 'files'> | null | undefined): number => {
  const total = cantidadArchivos(paso)
  const indices = new Set<number>()
  for (const archivo of paso?.files ?? []) {
    if (archivo && archivo.status !== 'REJECTED' && archivo.index >= 0 && archivo.index < total) indices.add(archivo.index)
  }
  return indices.size
}

// Índices rechazados del paso (cada uno cuenta una vez).
export const archivosRechazados = (paso: Pick<IFlow, 'fileCount' | 'files'> | null | undefined): number => {
  const total = cantidadArchivos(paso)
  const indices = new Set<number>()
  for (const archivo of paso?.files ?? []) {
    if (archivo && archivo.status === 'REJECTED' && archivo.index >= 0 && archivo.index < total) indices.add(archivo.index)
  }
  return indices.size
}

// Igual que estadoDelPaso del app-backend (app/services/flowDataArchivos.service.js):
// completo con al menos el mínimo de archivos válidos (no rechazados; todos, sin
// rango). Sin rango un rechazado lo deja incompleto, como siempre; con rango un
// opcional rechazado no lo regresa mientras queden los válidos del mínimo.
export const pasoArchivosCompleto = (paso: Pick<IFlow, 'fileCount' | 'fileCountMin' | 'files'> | null | undefined): boolean =>
  archivosCargados(paso) >= minimoArchivos(paso)

// Texto del avance de un paso multiarchivo (lista de pasos y encabezado de la
// carga), con las mismas palabras que el mensaje del backend al subir:
//   sin rango             «2 de 6 archivos»
//   con rango, falta      «3 de 4 necesarios (hasta 6)»
//   con rango, completo   «4 de 6 · completo»
export const textoAvanceArchivos = (cargados: number, minimo: number, total: number, completo: boolean): string => {
  if (minimo >= total) return `${cargados} de ${total} archivos`
  if (completo) return `${cargados} de ${total} · completo`
  if (cargados < minimo) return `${cargados} de ${minimo} necesarios (hasta ${total})`
  // Ya tiene el mínimo pero falta corregir alguno: sin «5 de 4».
  return `${cargados} de ${total} (necesitas ${minimo})`
}
